import { ChevronRight } from 'lucide-react'
import type { ContextualNav } from './MainNav'

interface BreadcrumbsProps {
  contextualNav: ContextualNav | null
  /** Fallback entity info when contextualNav is not available (e.g. events) */
  entityInfo?: { name: string; type: string } | null
  currentViewLabel?: string
  onNavigate?: (href: string) => void
}

const PARENT_LABELS: Record<string, { label: string; href: string }> = {
  association: { label: 'My Associations', href: '/associations' },
  circle: { label: 'My Circles', href: '/circles' },
  event: { label: 'My Events', href: '/events' },
}

export function Breadcrumbs({ contextualNav, entityInfo, currentViewLabel, onNavigate }: BreadcrumbsProps) {
  // Determine entity type and name from contextualNav or fallback entityInfo
  const entityType = contextualNav?.entityType ?? entityInfo?.type
  const entityName = contextualNav?.entityName ?? entityInfo?.name
  if (!entityType || !entityName) return null

  const parent = PARENT_LABELS[entityType]
  if (!parent) return null

  const activeItem = contextualNav?.items.find((i) => i.isActive)
  const viewLabel = currentViewLabel || activeItem?.label || 'Details'

  return (
    <nav aria-label="Breadcrumb" className="px-4 sm:px-6 lg:px-8 py-2 bg-slate-100/50 dark:bg-slate-900/50 border-b border-slate-200/50 dark:border-slate-800/50">
      <ol className="flex items-center gap-1.5 text-sm">
        <li>
          <button
            onClick={() => onNavigate?.(parent.href)}
            className="text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            {parent.label}
          </button>
        </li>
        <li aria-hidden="true">
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-600" />
        </li>
        <li>
          <span className="text-slate-700 dark:text-slate-300 font-medium truncate max-w-[200px] inline-block align-bottom" title={entityName}>
            {entityName}
          </span>
        </li>
        {viewLabel && (
          <>
            <li aria-hidden="true">
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-600" />
            </li>
            <li aria-current="page">
              <span className="text-slate-900 dark:text-white font-medium">
                {viewLabel}
              </span>
            </li>
          </>
        )}
      </ol>
    </nav>
  )
}
